/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { Progress, Rate, Typography } from 'antd';
import {
  LikeFilled,
  DislikeFilled,
  StarFilled,
  CommentOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';

type BinaryDashboardData = {
  total_reviews: number;
  binary_positive_count: number;
  binary_negative_count: number;
  total_comment: number;
  last_review_time: number;
  last_calculated_time: number;
};

type StarDashboardData = {
  total_reviews: number;
  total_ratings: number;
  avg_star_rating: number;
  star_1_count: number;
  star_2_count: number;
  star_3_count: number;
  star_4_count: number;
  star_5_count: number;
  total_comment: number;
  last_review_time: number;
  last_calculated_time: number;
};

type ReviewDashboardProps = {
  dashboardData: BinaryDashboardData | StarDashboardData | null;
  phase: 'screening' | 'evaluation';
};

export const ReviewDashboard: React.FC<ReviewDashboardProps> = ({
  dashboardData,
  phase,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  if (!dashboardData) {
    return null;
  }

  if (phase === 'screening') {
    return (
      <BinaryDashboard dashboardData={dashboardData as BinaryDashboardData} />
    );
  }

  return <StarDashboard dashboardData={dashboardData as StarDashboardData} />;
};

const BinaryDashboard: React.FC<{ dashboardData: BinaryDashboardData }> = ({
  dashboardData,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const {
    total_reviews,
    binary_positive_count,
    binary_negative_count,
  } = dashboardData;

  const likesPercentage =
    total_reviews > 0 ? (binary_positive_count / total_reviews) * 100 : 0;
  const dislikesPercentage =
    total_reviews > 0 ? (binary_negative_count / total_reviews) * 100 : 0;

  return (
    <div css={styles.dashboard}>
      <Typography.Text strong css={styles.dashboardTitle}>
        {intl.formatMessage({ id: 'review-dashboard-title' })}
      </Typography.Text>

      <div css={styles.dashboardContent}>
        <div css={styles.totalCount}>
          {intl.formatMessage(
            { id: 'review-dashboard-total' },
            { count: total_reviews }
          )}
        </div>

        {dashboardData.total_comment > 0 && (
          <div css={styles.commentCount}>
            <CommentOutlined rev={''} />
            <span>
              {intl.formatMessage(
                { id: 'review-dashboard-comments' },
                { count: dashboardData.total_comment }
              )}
            </span>
          </div>
        )}

        <div css={styles.binaryBreakdown}>
          {/* Header row with both labels */}
          <div css={styles.binaryHeader}>
            <div css={styles.binaryLabelGroup}>
              <LikeFilled css={styles.likeIcon} />
              <span css={styles.binaryLabelText}>
                {intl.formatMessage({ id: 'review-dashboard-likes' })}
              </span>
              <span css={styles.binaryCount}>
                <strong>{binary_positive_count}</strong>
                <span css={styles.percentage}>
                  ({Math.round(likesPercentage)}%)
                </span>
              </span>
            </div>

            <div css={styles.binaryLabelGroup}>
              <DislikeFilled css={styles.dislikeIcon} />
              <span css={styles.binaryLabelText}>
                {intl.formatMessage({ id: 'review-dashboard-dislikes' })}
              </span>
              <span css={styles.binaryCount}>
                <strong>{binary_negative_count}</strong>
                <span css={styles.percentage}>
                  ({Math.round(dislikesPercentage)}%)
                </span>
              </span>
            </div>
          </div>

          {/* Combined progress bar */}
          <div css={styles.binaryCombinedProgress}>
            <div
              css={styles.likesBar}
              style={{ width: `${likesPercentage}%` }}
            />
            <div
              css={styles.dislikesBar}
              style={{ width: `${dislikesPercentage}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const StarDashboard: React.FC<{ dashboardData: StarDashboardData }> = ({
  dashboardData,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const {
    total_reviews,
    avg_star_rating,
    star_5_count,
    star_4_count,
    star_3_count,
    star_2_count,
    star_1_count,
  } = dashboardData;

  const starDistribution = [
    { stars: 5, count: star_5_count },
    { stars: 4, count: star_4_count },
    { stars: 3, count: star_3_count },
    { stars: 2, count: star_2_count },
    { stars: 1, count: star_1_count },
  ];

  return (
    <div css={styles.dashboard}>
      <Typography.Text strong css={styles.dashboardTitle}>
        {intl.formatMessage({ id: 'review-dashboard-title' })}
      </Typography.Text>

      <div css={styles.dashboardContent}>
        <div css={styles.averageRating}>
          <div css={styles.averageRatingValue}>
            {avg_star_rating.toFixed(1)}
          </div>
          <div css={styles.averageRatingStars}>
            <Rate
              disabled
              allowHalf
              value={avg_star_rating}
              css={styles.rateComponent}
            />
          </div>
          <div css={styles.totalCount}>
            {intl.formatMessage(
              { id: 'review-dashboard-total' },
              { count: total_reviews }
            )}
          </div>

          {dashboardData.total_comment > 0 && (
            <div css={styles.commentCount}>
              <CommentOutlined rev={''} />
              <span>
                {intl.formatMessage(
                  { id: 'review-dashboard-comments' },
                  { count: dashboardData.total_comment }
                )}
              </span>
            </div>
          )}
        </div>

        <div css={styles.distributionSection}>
          <Typography.Text css={styles.distributionTitle}>
            {intl.formatMessage({ id: 'review-dashboard-distribution' })}
          </Typography.Text>

          <div css={styles.starDistribution}>
            {starDistribution.map(({ stars, count }) => {
              const percentage =
                total_reviews > 0 ? (count / total_reviews) * 100 : 0;

              return (
                <div key={stars} css={styles.starDistributionRow}>
                  <div css={styles.starLabel}>
                    <span css={styles.starNumber}>{stars}</span>
                    <StarFilled css={styles.starIconSmall} />
                  </div>
                  <div css={styles.starBar}>
                    <Progress
                      percent={percentage}
                      strokeColor="#faad14"
                      trailColor="#f0f0f0"
                      showInfo={false}
                      css={styles.starProgress}
                    />
                  </div>
                  <div css={styles.starCount}>{count}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  dashboard: {
    background: '#fafafa',
    borderRadius: 8,
    padding: 20,
    marginBottom: 24,
    border: '1px solid #e8e8e8',
  },

  dashboardTitle: {
    display: 'block',
    fontSize: 16,
    marginBottom: 16,
    color: '#262626',
  },

  dashboardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },

  totalCount: {
    fontSize: 14,
    color: '#595959',
    fontWeight: 500,
  },

  commentCount: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    color: '#8c8c8c',
    fontWeight: 400,
  },

  // Binary Dashboard Styles
  binaryBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  binaryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
  },

  binaryLabelGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },

  binaryLabelText: {
    fontSize: 14,
    fontWeight: 600,
    color: '#262626',
  },

  likeIcon: {
    fontSize: 16,
    color: '#52c41a',
    flexShrink: 0,
  },

  dislikeIcon: {
    fontSize: 16,
    color: '#ff4d4f',
    flexShrink: 0,
  },

  binaryCount: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 4,
    fontSize: 14,

    strong: {
      fontSize: 16,
    },
  },

  percentage: {
    fontSize: 12,
    color: '#8c8c8c',
  },

  binaryCombinedProgress: {
    display: 'flex',
    height: 24,
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
  },

  likesBar: {
    height: '100%',
    backgroundColor: '#52c41a',
    transition: 'width 0.3s ease',
  },

  dislikesBar: {
    height: '100%',
    backgroundColor: '#ff4d4f',
    transition: 'width 0.3s ease',
  },

  // Star Dashboard Styles
  averageRating: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '16px 0',
    borderBottom: '1px solid #e8e8e8',
  },

  averageRatingValue: {
    fontSize: 42,
    fontWeight: 700,
    color: '#262626',
    lineHeight: 1,
  },

  averageRatingStars: {
    display: 'flex',
    gap: 4,
  },

  starIconFilled: {
    fontSize: 18,
    color: '#faad14',
  },

  starIconEmpty: {
    fontSize: 18,
    color: '#d9d9d9',
  },

  rateComponent: {
    fontSize: 18,

    '.ant-rate-star': {
      marginRight: 4,
    },
  },

  distributionSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  distributionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#595959',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  starDistribution: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },

  starDistributionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },

  starLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: 30,
    fontSize: 13,
    fontWeight: 500,
    color: '#595959',
  },

  starNumber: {
    display: 'inline-block',
    width: 12,
    textAlign: 'right',
  },

  starIconSmall: {
    fontSize: 12,
    color: '#faad14',
  },

  starBar: {
    flex: 1,
  },

  starProgress: {
    margin: 0,

    '.ant-progress-bg': {
      height: '8px !important',
      borderRadius: 4,
    },

    '.ant-progress-inner': {
      borderRadius: 4,
      backgroundColor: '#f0f0f0',
    },
  },

  starCount: {
    minWidth: 50,
    width: 50,
    textAlign: 'center',
    fontSize: 13,
    fontWeight: 500,
    color: '#595959',
  },
});
